import { nip19 } from 'nostr-tools';

export type SearchType = 'profile' | 'note' | 'hashtag' | 'relay' | 'text';

export interface SearchDetection {
  type: SearchType[];
  value: string;
  isHex?: boolean;
  isNip19?: boolean;
  nip19Type?: string;
}

/**
 * Detects the type of search query and what search operations to perform
 */
export function detectSearchType(query: string): SearchDetection {
  const trimmed = query.trim();
  if (!trimmed) return { type: [], value: '' };

  // Remove nostr: prefix if present
  const cleanQuery = trimmed.replace(/^nostr:/, '');

  // Check for hex ID (64 character hex string)
  if (/^[0-9a-f]{64}$/i.test(cleanQuery)) {
    return {
      type: ['note', 'profile'],
      value: cleanQuery,
      isHex: true,
    };
  }

  // Check for NIP-19 identifiers
  if (cleanQuery.match(/^(npub1|nprofile1|note1|nevent1|naddr1)/)) {
    try {
      const decoded = nip19.decode(cleanQuery);
      const types: SearchType[] = [];

      if (decoded.type === 'npub' || decoded.type === 'nprofile') {
        types.push('profile');
      } else if (decoded.type === 'note' || decoded.type === 'nevent') {
        types.push('note');
      } else if (decoded.type === 'naddr') {
        types.push('note');
      }

      return {
        type: types,
        value: cleanQuery,
        isNip19: true,
        nip19Type: decoded.type,
      };
    } catch {
      // If decode fails, treat as text search
    }
  }

  // Check for hashtag
  if (cleanQuery.startsWith('#')) {
    return {
      type: ['hashtag'],
      value: cleanQuery.slice(1),
    };
  }

  // Check for relay URL
  if (cleanQuery.match(/^wss?:\/\//i)) {
    return {
      type: ['relay'],
      value: cleanQuery,
    };
  }

  // Check if it looks like a partial relay URL
  if (cleanQuery.match(/^wss?:?$/i)) {
    return {
      type: ['relay'],
      value: cleanQuery,
    };
  }

  // Default to text search (profiles and content)
  return {
    type: ['profile', 'text'],
    value: cleanQuery,
  };
}

/**
 * Normalizes relay URL
 */
export function normalizeRelayUrl(url: string): string {
  let normalized = url.trim();

  // Add protocol if missing
  if (!normalized.match(/^wss?:\/\//i)) {
    if (normalized.startsWith('ws:')) {
      normalized = 'ws://' + normalized.slice(3);
    } else if (normalized.startsWith('wss:')) {
      normalized = 'wss://' + normalized.slice(4);
    } else {
      normalized = 'wss://' + normalized;
    }
  }

  // Remove trailing slash
  normalized = normalized.replace(/\/$/, '');

  return normalized;
}
